import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { Box, Container, Heading, Text, Tabs, TabList, TabPanels, Tab, TabPanel, Flex, Badge, Stack, Spinner, Grid, IconButton, ButtonGroup, Button, Link, Image, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useToast, Input, useColorModeValue, Tooltip } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { addMonths, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, addDays, parse } from 'date-fns';
import { cs } from 'date-fns/locale';
import { getCompetitionAliasesPublic, CompetitionAlias } from '../services/competitionAliases';
import { useSearchParams } from 'react-router-dom';
import { useCountdown, useMultipleCountdowns } from '../hooks/useCountdown';
import NewsletterCTA from '../components/common/NewsletterCTA';
import { sortCategoriesWithOrder } from '../utils/categorySort';
import ClubModal from '../components/home/ClubModal';
import { assetUrl } from '../utils/url';
import { API_URL } from '../services/api';
import { TeamLogo } from '../components/common/TeamLogo';
import { useTranslation } from 'react-i18next';
import { MatchWeatherLazy } from '../components/common/MatchWeatherLazy';
import { MatchWeather } from '../components/common/MatchWeather';

// Dynamic weekday headers based on language
const getWeekdayHeaders = (language: string): string[] => {
  if (language === 'en') {
    return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }
  // Default to Czech
  return ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
};

type MatchItem = {
  id: number | string;
  date: string; // yyyy-mm-dd
  time: string; // HH:MM
  home: string;
  away: string;
  home_id?: string;
  away_id?: string;
  venue?: string;
  home_logo_url?: string;
  away_logo_url?: string;
  report_url?: string;
  facr_link?: string;
  score?: string; // optional: e.g., "2:1" for finished matches
  // enriched when building the "Všechny soutěže" tab
  __compId?: string;
  __compName?: string;
  __compDisplayOrder?: number;
};

type Competition = {
  id: string;
  name: string;
  code?: string;
  matches: MatchItem[];
};

const CalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [compLinks, setCompLinks] = useState<Record<string, string>>({});
  const [aliasMap, setAliasMap] = useState<Record<string, { alias: string; original_name?: string; display_order?: number }>>({});
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [clubName, setClubName] = useState<string>('');
  const [clubId, setClubId] = useState<string>('');
  const [clubType, setClubType] = useState<'football' | 'futsal'>('football');
  const [standings, setStandings] = useState<any[]>([]);

  // Get weekday headers based on current language
  const weekdayHeaders = getWeekdayHeaders(i18n.language);

  // Helper functions inside component to access t()
  const getSentiment = (m: MatchItem): { label: string; color: string } | null => {
    // Don't show sentiment for future matches
    const dt = new Date(`${m.date}T${(m.time || '00:00')}:00`);
    const isPast = Date.now() >= dt.getTime();
    if (!isPast) return null;
    
    const s = parseScore(m.score);
    if (!s) return null;
    
    const ourIsHome = normalize(m.home).includes(normalize(clubName));
    const ourIsAway = normalize(m.away).includes(normalize(clubName));
    
    if (!ourIsHome && !ourIsAway) return null; // unknown perspective
    if (s.h === s.a) return { label: t('calendar.draw'), color: 'blue' };
    const ourGoals = ourIsHome ? s.h : s.a;
    const oppGoals = ourIsHome ? s.a : s.h;
    if (ourGoals > oppGoals) return { label: t('calendar.win'), color: 'green' };
    return { label: t('calendar.loss'), color: 'red' };
  };

  // Active competition for current tab (memoized)
  const activeCompetition = useMemo(() => competitions[tabIndex], [competitions, tabIndex]);

  // Color mode values for dark/light theme
  const calendarDayBg = useColorModeValue('white', 'gray.800');
  const calendarDayBorder = useColorModeValue('gray.200', 'gray.700');
  const calendarMatchBg = useColorModeValue('gray.50', 'gray.700');
  const calendarMatchHoverBg = useColorModeValue('blue.50', 'blue.900');
  const listGroupBg = useColorModeValue('white', 'gray.800');
  const listGroupBorder = useColorModeValue('gray.200', 'gray.700');
  const listGroupHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const listGroupHeaderBgHighlight = useColorModeValue('blue.50', 'blue.900');
  const listGroupHeaderBorderLeft = useColorModeValue('gray.300', 'gray.600');
  const listGroupHeaderText = useColorModeValue('gray.800', 'gray.100');
  const listMatchBg = useColorModeValue('gray.50', 'gray.700');
  const listMatchBorder = useColorModeValue('gray.200', 'gray.600');
  const listMatchHoverBg = useColorModeValue('blue.50', 'blue.900');
  const listDateText = useColorModeValue('gray.800', 'gray.100');
  const listVenueText = useColorModeValue('gray.600', 'gray.400');
  const listTimeText = useColorModeValue('gray.700', 'gray.300');

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<{ match: MatchItem; comp?: Competition } | null>(null);
  // Removed admin notification form state (moved to admin page)
  // Fan subscribe state
  const [fanEmail, setFanEmail] = useState<string>("");
  const [fanSubscribing, setFanSubscribing] = useState<boolean>(false);

  // ClubModal state
  const [isClubModalOpen, setIsClubModalOpen] = useState<boolean>(false);
  const [selectedClub, setSelectedClub] = useState<any | null>(null);

  const resolveBackendUrl = (path: string) => {
    try {
      if (/^https?:\/\//i.test(path)) return path;
      if (path.startsWith('/cache') || path.startsWith('/uploads') || path.startsWith('/api/')) {
        const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        return new URL(path, origin).toString();
      }
      return path;
    } catch {
      return path;
    }
  };

  // Helper functions shared across effects and render helpers
  const normalize = (s: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const stripPrefixes = (s: string) => {
    let x = normalize(s);
    x = x.replace(/\b(mestsky|m\.?f\.?k\.?|mfk|tj|sk|sokol|fotbalovy|fotbalový|fotbalovy\s+klub|fotbalovy\s+klub)\b/g, '');
    return x.replace(/\s+/g, ' ').trim();
  };

  const subscribeFan = async () => {
    if (!fanEmail) {
      toast({ title: 'Zadejte email', status: 'warning' });
      return;
    }
    setFanSubscribing(true);
    try {
      const res = await fetch(resolveBackendUrl('/api/v1/newsletter/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fanEmail })
      });
      if (!res.ok) {
        const jt = await res.json().catch(() => ({} as any));
        throw new Error(jt?.error || `HTTP ${res.status}`);
      }
      toast({ title: 'Přihlášeno k odběru', status: 'success' });
      setFanEmail('');
    } catch (e: any) {
      toast({ title: 'Chyba přihlášení', description: e?.message || String(e), status: 'error' });
    } finally {
      setFanSubscribing(false);
    }
  };

  // Handle team click to show team stats in ClubModal
  const handleTeamClick = (teamName: string, teamLogoUrl?: string) => {
    try {
      const normalizedTeamName = normalize(teamName);
      
      // Search through all standings to find the team
      let foundTeam: any = null;
      for (const standing of standings) {
        const table = standing.table || standing.rows || [];
        for (const row of table) {
          const rowTeamName = normalize(row.team_name || row.team || '');
          if (rowTeamName === normalizedTeamName || rowTeamName.includes(normalizedTeamName) || normalizedTeamName.includes(rowTeamName)) {
            foundTeam = row;
            break;
          }
        }
        if (foundTeam) break;
      }

      // If team found in standings, show detailed modal
      if (foundTeam) {
        const clubData = {
          team: foundTeam.team_name || foundTeam.team || teamName,
          team_id: foundTeam.team_id || String(foundTeam.position || 0),
          team_logo_url: foundTeam.logo_url ? (assetUrl(foundTeam.logo_url) || foundTeam.logo_url) : teamLogoUrl,
          rank: foundTeam.position || foundTeam.rank || foundTeam.pos,
          played: foundTeam.played || foundTeam.matches,
          wins: foundTeam.wins || foundTeam.win,
          draws: foundTeam.draws || foundTeam.draw,
          losses: foundTeam.losses || foundTeam.loss,
          score: foundTeam.score || ((foundTeam.goals_for || foundTeam.gf) && (foundTeam.goals_against || foundTeam.ga) 
            ? `${foundTeam.goals_for || foundTeam.gf}:${foundTeam.goals_against || foundTeam.ga}` 
            : undefined),
          points: foundTeam.points || foundTeam.pts,
        };
        setSelectedClub(clubData);
        setIsClubModalOpen(true);
      } else {
        // If not found in standings, show minimal modal with just the name and logo
        const minimalClubData = {
          team: teamName,
          team_id: teamName,
          team_logo_url: teamLogoUrl,
          rank: '-',
          played: '-',
          wins: '-',
          draws: '-',
          losses: '-',
          score: '-',
          points: '-',
        };
        setSelectedClub(minimalClubData);
        setIsClubModalOpen(true);
      }
    } catch {
      // noop
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Load public competition aliases for display
        let amap: Record<string, { alias: string; original_name?: string; display_order?: number }> = {};
        try {
          const aliases: CompetitionAlias[] = await getCompetitionAliasesPublic();
          (aliases || []).forEach((a) => { if (a?.code && a?.alias) amap[a.code] = { alias: a.alias, original_name: a.original_name, display_order: a.display_order }; });
        } catch {}

        // Load team-logo overrides (API + fallback to cached file)
        const now = Date.now();
        let overrides: any = null;
        try {
          const res = await fetch(resolveBackendUrl(`/api/v1/public/team-logo-overrides?t=${now}`), { cache: 'no-cache' });
          if (res.ok) overrides = await res.json();
        } catch {}
        if (!overrides) {
          try {
            const res2 = await fetch(resolveBackendUrl('/cache/prefetch/team_logo_overrides.json'), { cache: 'no-cache' });
            if (res2.ok) overrides = await res2.json();
          } catch {}
        }
        const byName: Record<string, string> = (overrides?.by_name || {}) as any;
        const byId: Record<string, { name?: string; logo_url?: string }> = (overrides?.by_id || {}) as any;
        const byNameNormalized: Record<string, string> = Object.keys(byName || {}).reduce((acc: Record<string, string>, k: string) => { acc[normalize(k)] = byName[k]; return acc; }, {});
        const byNameStrippedPairs: Array<{ keyNorm: string; url: string }> = Object.keys(byName || {}).map((k: string) => ({ keyNorm: stripPrefixes(k), url: byName[k] }));
        const normName = (s?: string) => String(s || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-')
          .replace(/\bn\.?\b/g, ' nad ')
          .replace(/\bp\.?\b/g, ' pod ')
          .replace(/[\,\s]*(z\.?\s*s\.?|o\.?\s*s\.?)\s*$/g, '')
          .replace(/[\.,!;:()\[\]{}]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        const aliasNameIndex: Record<string, string> = (() => {
          const urlToName: Record<string, string> = {};
          for (const v of Object.values(byId || {})) {
            const nm = String((v as any)?.name || '').trim();
            const lg = String((v as any)?.logo_url || '').trim();
            if (nm && lg) urlToName[lg] = nm;
          }
          const idx: Record<string, string> = {};
          for (const [alias, url] of Object.entries(byName || {})) {
            const canon = urlToName[String(url)] || '';
            const key = normName(alias);
            if (canon && key) idx[key] = canon;
          }
          return idx;
        })();
        const nameIndex: Record<string, { id: string; name: string; logo_url: string }> = (() => {
          const idx: Record<string, { id: string; name: string; logo_url: string }> = {};
          for (const [id, v] of Object.entries(byId || {})) {
            const nm = String((v as any)?.name || '').trim();
            const lg = String((v as any)?.logo_url || '').trim();
            if (!nm) continue;
            const key = normName(nm);
            if (!key) continue;
            idx[key] = { id, name: nm, logo_url: lg } as any;
          }
          return idx;
        })();
        const getOverrideName = (teamName?: string, teamId?: string) => {
          const tid = teamId ? String(teamId) : '';
          if (tid && byId?.[tid]?.name && String(byId[tid].name).trim()) {
            return String(byId[tid].name).trim();
          }
          try {
            const n = normName(teamName);
            if (aliasNameIndex[n]) return aliasNameIndex[n];
            let hit: any = nameIndex[n];
            if (!hit) {
              for (const [k, v] of Object.entries(nameIndex)) {
                if (!k) continue;
                if (n.endsWith(k) || k.endsWith(n)) { hit = v; break; }
              }
            }
            if (hit && (hit as any).name) return String((hit as any).name);
          } catch {}
          return String(teamName || '');
        };
        const getOverrideLogo = (teamName?: string, original?: string, teamId?: string) => {
          // Prefer admin override by ID
          if (teamId && byId?.[teamId]?.logo_url) {
            const v = byId[teamId]!.logo_url as string;
            if (typeof v === 'string' && v.startsWith('/')) return resolveBackendUrl(v);
            return v;
          }
          if (!teamName) return original;
          const exact = (byName || {})[teamName];
          const normName = normalize(teamName);
          let candidate = exact || byNameNormalized[normName];
          if (!candidate) {
            const stripped = stripPrefixes(teamName);
            for (const { keyNorm, url } of byNameStrippedPairs) {
              if (!keyNorm) continue;
              if (stripped.endsWith(keyNorm) || keyNorm.endsWith(stripped)) { candidate = url; break; }
            }
          }
          const chosen = candidate || original;
          if (typeof chosen === 'string' && chosen.startsWith('/')) return resolveBackendUrl(chosen);
          return chosen;
        };
        const res = await fetch(resolveBackendUrl('/cache/prefetch/facr_club_info.json'), { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const compLinkMap: Record<string,string> = {};
        const baseComps: Competition[] = Array.isArray(json?.competitions)
          ? json.competitions.map((c: any, cIdx: number) => {
              const compId = String(c.id || cIdx);
              if (c.matches_link) compLinkMap[compId] = String(c.matches_link);
              const matches: MatchItem[] = (Array.isArray(c.matches) ? c.matches : []).map((m: any, idx: number) => {
                const dt: string = String(m.date_time || '');
                const [d, t] = dt.includes(' ') ? dt.split(' ') : [dt, ''];
                const [day, month, year] = d.split('.');
                const isoDate = (day && month && year) ? `${year}-${month.padStart(2,'0')}-${day.padStart(2,'0')}` : new Date().toISOString().slice(0,10);
                const time = (t || '00:00').slice(0,5);
                const score = (m.score || m.result || (typeof m.goals_home === 'number' && typeof m.goals_away === 'number' ? `${m.goals_home}:${m.goals_away}` : '') || '').toString();
                const homeName = getOverrideName(m.home, m.home_id);
                const awayName = getOverrideName(m.away, m.away_id);
                return {
                  id: m.match_id || `${cIdx}-${idx}`,
                  date: isoDate,
                  time,
                  home: homeName,
                  away: awayName,
                  home_id: m.home_id,
                  away_id: m.away_id,
                  venue: m.venue,
                  home_logo_url: getOverrideLogo(homeName, m.home_logo_url, m.home_id),
                  away_logo_url: getOverrideLogo(awayName, m.away_logo_url, m.away_id),
                  report_url: m.report_url,
                  facr_link: m.facr_link,
                  score: score && /\d+\s*:\s*\d+/.test(score) ? score.replace(/\s+/g,'') : undefined,
                };
              })
              .sort((a: MatchItem, b: MatchItem) => new Date(`${a.date}T${a.time}:00`).getTime() - new Date(`${b.date}T${b.time}:00`).getTime());
              return {
                id: compId,
                code: c.code,
                name: (amap?.[c?.code]?.alias) || (amap?.[compId]?.alias) || c.name || c.code || `Soutěž ${cIdx+1}`,
                matches,
              };
            })
          : [];

        // Try to enrich competitions missing matches using facr detailed info file
        let enriched: Competition[] = baseComps;
        try {
          const clubId = json?.club_id;
          const clubType = json?.club_type || 'football';
          if (clubId) {
            const infoPath = `/cache/facr/${clubType}_${clubId}_info.json`;
            const res2 = await fetch(resolveBackendUrl(infoPath), { cache: 'no-cache' });
            if (res2.ok) {
              const j2 = await res2.json();
              if (Array.isArray(j2?.competitions)) {
                const mapCompMatches = (c: any, cIdx: number) => {
                  const list: MatchItem[] = (Array.isArray(c.matches) ? c.matches : []).map((m: any, idx: number) => {
                    const dt: string = String(m.date_time || '');
                    const [d, t] = dt.includes(' ') ? dt.split(' ') : [dt, ''];
                    const [day, month, year] = d.split('.');
                    const isoDate = (day && month && year) ? `${year}-${month.padStart(2,'0')}-${day.padStart(2,'0')}` : new Date().toISOString().slice(0,10);
                    const time = (t || '00:00').slice(0,5);
                    const score = (m.score || m.result || (typeof m.goals_home === 'number' && typeof m.goals_away === 'number' ? `${m.goals_home}:${m.goals_away}` : '') || '').toString();
                    return {
                      id: m.match_id || `${cIdx}-${idx}`,
                      date: isoDate,
                      time,
                      home: getOverrideName(m.home, m.home_id),
                      away: getOverrideName(m.away, m.away_id),
                      home_id: m.home_id,
                      away_id: m.away_id,
                      venue: m.venue,
                      home_logo_url: getOverrideLogo(m.home, m.home_logo_url, m.home_id),
                      away_logo_url: getOverrideLogo(m.away, m.away_logo_url, m.away_id),
                      report_url: m.report_url,
                      score: score && /\d+\s*:\s*\d+/.test(score) ? score.replace(/\s+/g,'') : undefined,
                    } as MatchItem;
                  }).sort((a: MatchItem, b: MatchItem) => new Date(`${a.date}T${a.time}:00`).getTime() - new Date(`${b.date}T${b.time}:00`).getTime());
                  return list;
                };
                enriched = baseComps.map((bc) => {
                  if (bc.matches.length) return bc;
                  const found = j2.competitions.find((c: any) => String(c.id) === String(bc.id) || c.name === bc.name || c.code === bc.name || c.code === bc.id);
                  if (!found) return bc;
                  const mm = mapCompMatches(found, 0);
                  return { ...bc, matches: mm };
                });
              }
            }
          }
        } catch {}

        // Sort competitions by age (Muži first, then U19, U17, etc.) and respect custom order
        const sortedEnriched = sortCategoriesWithOrder(
          enriched.map(c => ({
            ...c,
            alias: (amap?.[c?.code || '']?.alias) || (amap?.[c?.id]?.alias),
            display_order: (amap?.[c?.code || '']?.display_order) ?? (amap?.[c?.id]?.display_order),
          }))
        );

        // Build unified "All competitions" entry as the default tab
        const allMatches: MatchItem[] = (sortedEnriched || [])
          .flatMap((c, idx) => (c.matches || []).map(m => ({ 
            ...m, 
            __compId: c.id, 
            __compName: c.name,
            __compDisplayOrder: c.display_order ?? (1000 + idx) // Use display_order or fallback to sorted index
          } as MatchItem)))
          .sort((a, b) => new Date(`${a.date}T${a.time}:00`).getTime() - new Date(`${b.date}T${b.time}:00`).getTime());
        const allComp: Competition = { id: 'all', name: t('calendar.all_competitions'), matches: allMatches };

        // Load standings data
        let standingsData: any[] = [];
        try {
          const tablesRes = await fetch(resolveBackendUrl('/cache/prefetch/facr_tables.json'), { cache: 'no-cache' });
          if (tablesRes.ok) {
            const facrTablesJSON = await tablesRes.json();
            if (facrTablesJSON?.competitions?.length) {
              standingsData = (facrTablesJSON.competitions || []).map((c: any) => ({
                name: (amap?.[c?.code]?.alias) || (amap?.[c?.id]?.alias) || c.name || c.code,
                table: (c.table?.overall || []).map((r: any, idx: number) => {
                  const teamName = ((): string => {
                    const t = r.team;
                    if (typeof t === 'string') return t;
                    if (t && typeof t === 'object') {
                      return t.name || t.Name || t.team_name || t.TeamName || '-';
                    }
                    return r.team_name || r.TeamName || '-';
                  })();
                  const teamId = ((): string | undefined => {
                    const t = r.team;
                    if (t && typeof t === 'object') {
                      return t.id || t.team_id || t.teamId || r.team_id;
                    }
                    return r.team_id;
                  })();
                  return {
                    position: Number(r.rank || idx + 1),
                    team_name: (teamId && byId?.[teamId]?.name && String(byId[teamId].name).trim()) ? String(byId[teamId].name) : teamName,
                    team_id: teamId,
                    points: Number(r.points || r.pts || 0),
                    played: Number(r.played || r.matches || 0),
                    wins: Number(r.wins || r.win || 0),
                    draws: Number(r.draws || r.draw || 0),
                    losses: Number(r.losses || r.loss || 0),
                    goals_for: Number(r.goals_for ?? r.gf ?? r.goalsFor ?? r.scored ?? r.goals ?? 0),
                    goals_against: Number(r.goals_against ?? r.ga ?? r.goalsAgainst ?? r.conceded ?? 0),
                    logo_url: (teamId && byId?.[teamId]?.logo_url) ? String(byId[teamId].logo_url) : (r.team_logo_url || undefined),
                  };
                }),
              }));
            }
          }
        } catch {}

        if (!cancelled) {
          setAliasMap(amap);
          const comps = [allComp, ...sortedEnriched];
          setCompetitions(comps);
          setCompLinks(compLinkMap);
          setStandings(standingsData);
          if (json?.name) setClubName(String(json.name));
          if (json?.club_internal_id) setClubId(String(json.club_internal_id));
          if (json?.club_type) setClubType(json.club_type);
          // Set active tab from query ?comp=<id>
          const compQ = searchParams.get('comp');
          if (compQ) {
            const idx = comps.findIndex(cmp => String(cmp.id) === String(compQ));
            setTabIndex(idx >= 0 ? idx : 0);
          } else {
            setTabIndex(0);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Nepodařilo se načíst kalendář.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Optimized countdown hooks
  const modalCountdown = useCountdown(
    selected ? `${selected.match.date}T${selected.match.time || '00:00'}:00` : null,
    1000 // Update every second for modal
  );

  // Get upcoming matches for live countdowns (only future matches)
  const upcomingMatches = useMemo(() => {
    const list = activeCompetition?.matches || [];
    return list.filter(match => {
      const matchTime = new Date(`${match.date}T${match.time || '00:00'}:00`).getTime();
      return matchTime > Date.now();
    });
  }, [activeCompetition]);

  const liveCountdowns = useMultipleCountdowns(upcomingMatches, 30000); // Update every 30 seconds for better performance

  const openMatchModal = (match: MatchItem, comp?: Competition) => {
    setSelected({ match, comp });
    onOpen();
  };

  // Handle middle click to open in new tab
  const handleMatchMouseDown: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement> = (e) => {
    // Middle click or Ctrl/Cmd+Click should open link in new tab naturally when using <a>.
    // This handler is a safety net when match is rendered as a div.
    if (e.button === 1) {
      const target = e.currentTarget as HTMLElement & { dataset?: any };
      const href = (target.getAttribute && target.getAttribute('data-href')) || (target as any).dataset?.href;
      if (href) {
        try {
          e.preventDefault();
          e.stopPropagation();
          const link = document.createElement('a');
          link.href = href as string;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => { try { window.focus(); } catch {} }, 0);
        } catch {
          window.open(href as string, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };

  const hasData = useMemo(() => competitions.some(c => c.matches.length), [competitions]);

  // Month-view state and helpers
  const [monthRef, setMonthRef] = useState<Date>(startOfMonth(new Date()));
  const [viewMode, setViewMode] = useState<'calendar'|'list'>('calendar');
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
  const [showPast, setShowPast] = useState<boolean>(false);
  // Compute today's date string in Prague timezone once and reuse
  const pragueTodayStr = useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat('cs-CZ', {
        timeZone: 'Europe/Prague',
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      const y = parts.find(p => p.type === 'year')?.value;
      const m = parts.find(p => p.type === 'month')?.value;
      const d = parts.find(p => p.type === 'day')?.value;
      if (y && m && d) return `${y}-${m}-${d}`;
    } catch {}
    return format(new Date(), 'yyyy-MM-dd');
  }, []);
  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthRef), { weekStartsOn: 1 });
    // Build 6 weeks x 7 days
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(addDays(start, i));
    }
    return days;
  }, [monthRef]);

  const groupByDate = (matches: MatchItem[]) => {
    const map = new Map<string, MatchItem[]>();
    matches.forEach((m) => {
      const key = m.date;
      const arr = map.get(key) || [];
      arr.push(m);
      map.set(key, arr);
    });
    // Sort matches within each day by competition order, then by time
    map.forEach((matchList, dateKey) => {
      matchList.sort((a, b) => {
        // First sort by competition display order (if available)
        const aOrder = a.__compDisplayOrder ?? 9999;
        const bOrder = b.__compDisplayOrder ?? 9999;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        // Then sort by time
        const aTime = a.time || '00:00';
        const bTime = b.time || '00:00';
        return aTime.localeCompare(bTime);
      });
    });
    return map;
  };

  const getMatchCompInfo = (m: MatchItem, comp?: Competition): { display: string; alias?: string } => {
    try {
      let baseComp: Competition | undefined = comp;
      if ((comp?.id === 'all' || !comp) && m.__compId) {
        baseComp = competitions.find((cc) => String(cc.id) === String(m.__compId));
      }
      const display = (m.__compName) || (baseComp?.name || '');
      const alias = baseComp?.code ? (aliasMap[baseComp.code]?.alias) : undefined;
      return { display, alias };
    } catch {
      return { display: m.__compName || comp?.name || '', alias: undefined };
    }
  };

  // Sentiment helpers
  const isClubTeam = (team: string) => {
    try {
      const a = stripPrefixes(team);
      const b = stripPrefixes(clubName || '');
      if (!a || !b) return false;
      // Allow equality or suffix match (handles prefixes like TJ, SK, etc.)
      // Also handle common abbreviations like "FK" vs "Fotbalový klub"
      const aClean = a.replace(/\bfk\b/g, '').replace(/\bfotbalový\s+klub\b/g, '').trim();
      const bClean = b.replace(/\bfk\b/g, '').replace(/\bfotbalový\s+klub\b/g, '').trim();
      return a === b || a.endsWith(b) || b.endsWith(a) || aClean === bClean || aClean.endsWith(bClean) || bClean.endsWith(aClean);
    } catch { return false; }
  };
  const parseScore = (score?: string): { h: number; a: number } | null => {
    if (!score) return null;
    const m = score.match(/^(\d+)\s*[:\-]\s*(\d+)$/);
    if (!m) return null;
    return { h: parseInt(m[1], 10), a: parseInt(m[2], 10) };
  };

  return (
    <MainLayout>
      <Container maxW="7xl" py={{ base: 6, md: 10 }}>
        <Heading as="h1" size="xl" mb={2}>{t('calendar.title')}</Heading>
        <Text color="gray.600" mb={6}>{t('calendar.subtitle')}</Text>

        {loading && (
          <Flex align="center" gap={3} color="gray.600" mb={6}>
            <Spinner size="sm" />
            <span>{t('calendar.loading')}</span>
          </Flex>
        )}

        {error && (
          <Box color="red.600" mb={4}>{error}</Box>
        )}

        {!loading && !hasData && !error && (
          <Box color="gray.600">{t('calendar.no_matches')}</Box>
        )}

        {!!competitions.length && (
          <Tabs variant="soft-rounded" colorScheme="blue" index={tabIndex} onChange={(i) => setTabIndex(i)} isLazy lazyBehavior="keepMounted">
            {/* Compact, wrapped TabList with better spacing (no overlap) */}
            <Box mb={3} position="relative" zIndex={1}>
              <TabList
                flexWrap="wrap"
                gap={2}
                justifyContent="flex-start"
                sx={{
                  '& > button': {
                    minW: 'auto',
                    px: { base: 3, md: 4 },
                    py: { base: 1.5, md: 2 },
                    fontSize: { base: 'xs', md: 'sm' },
                    fontWeight: 600,
                    borderRadius: 'md',
                    transition: 'all 0.2s',
                    _selected: {
                      bg: 'brand.primary',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: 'md'
                    },
                    _hover: {
                      transform: 'translateY(-1px)',
                      boxShadow: 'sm'
                    }
                  }
                }}
              >
                {competitions.map((c) => (
                  <Tab key={c.id}>
                    {c.name}
                  </Tab>
                ))}
              </TabList>
            </Box>
            <TabPanels>
              {competitions.map((c, idx) => {
                const isActive = idx === tabIndex;
                const byDate = isActive ? groupByDate(c.matches) : (new Map() as Map<string, MatchItem[]>);
                const mkHref = (m: MatchItem) => (m.facr_link || m.report_url || undefined) ?? (`/zapas/${m.id}`);
                // Build latest results (only matches with score)
                const nowTs = isActive ? Date.now() : 0;
                const compareByDateDesc = (a: MatchItem, b: MatchItem) => new Date(`${b.date}T${(b.time||'00:00')}:00`).getTime() - new Date(`${a.date}T${(a.time||'00:00')}:00`).getTime();
                let latestResults: MatchItem[] = [];
                if (isActive) {
                  if (c.id === 'all') {
                    // For 'all', pick most recent scored match per competition
                    const grouped: Record<string, MatchItem[]> = {};
                    (c.matches || []).forEach((m) => {
                      if (!m.score) return;
                      const ts = new Date(`${m.date}T${(m.time||'00:00')}:00`).getTime();
                      if (isNaN(ts) || ts > nowTs) return; // future results not allowed
                      const key = m.__compId || 'na';
                      grouped[key] = grouped[key] || [];
                      grouped[key].push(m);
                    });
                    latestResults = Object.values(grouped)
                      .map(list => list.sort(compareByDateDesc)[0])
                      .filter(Boolean)
                      .sort(compareByDateDesc);
                  } else {
                    // Single competition: pick the most recent scored match
                    latestResults = (c.matches || [])
                      .filter(m => !!m.score && new Date(`${m.date}T${(m.time||'00:00')}:00`).getTime() <= nowTs)
                      .sort(compareByDateDesc)
                      .slice(0, 1);
                  }
                }
                return (
                  <TabPanel key={c.id} px={0}>
                    {/* Latest results header list rendered above both calendar and list modes */}
                    {isActive && latestResults.length > 0 && (
                      <Box mb={4}>
                        <Heading as="h3" size="md" mb={2}>Nejnovější výsledky</Heading>
                        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={3}>
                          {latestResults.map((m) => {
                            const href = mkHref(m);
                            const info = getMatchCompInfo(m, c);
                            return (
                              <Tooltip
                                key={`latest-${c.id}-${m.id}`}
                                label={(
                                  <Box>
                                    <Text fontSize="xs">Kategorie: {info.display || '—'}</Text>
                                    {info.alias && info.alias !== info.display && (
                                      <Text fontSize="xs">Alias: {info.alias}</Text>
                                    )}
                                  </Box>
                                )}
                                hasArrow
                                placement="top"
                                openDelay={200}
                              >
                                <Box position="relative" data-href={href || undefined} onMouseDown={handleMatchMouseDown} onClick={() => openMatchModal(m, c)} borderWidth="1px" borderRadius="md" p={2} _hover={{ textDecoration: 'none', bg: 'rgba(0,0,0,0.03)', borderColor: 'brand.primary', cursor: 'pointer' }}>
                                  {href && (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => { if (!(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) e.preventDefault(); }}
                                      onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                      onAuxClick={(e) => { if ((e as any).button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                      style={{ position: 'absolute', inset: 0, zIndex: 1 }}
                                      aria-hidden
                                    />
                                  )}
                                  <Flex align="center" justify="space-between" mb={2}>
                                    <Text fontSize="sm" color="gray.700">{m.date} {m.time || ''}</Text>
                                    <Badge colorScheme="purple">{m.__compName || c.name}</Badge>
                                  </Flex>
                                  <Flex align="center" gap={2} justify="center">
                                    <TeamLogo
                                      teamId={m.home_id}
                                      teamName={m.home}
                                      facrLogo={m.home_logo_url}
                                      size="custom"
                                      boxSize="18px"
                                      alt={m.home}
                                      borderRadius="full"
                                    />
                                    <Text fontSize="sm">{m.home}</Text>
                                    <Badge colorScheme={getSentiment(m)?.color || 'gray'}>{m.score || 'vs'}</Badge>
                                    <TeamLogo
                                      teamId={m.away_id}
                                      teamName={m.away}
                                      facrLogo={m.away_logo_url}
                                      size="custom"
                                      boxSize="18px"
                                      alt={m.away}
                                      borderRadius="full"
                                    />
                                    <Text fontSize="sm">{m.away}</Text>
                                  </Flex>
                                  {href && <Link href={href} isExternal onClick={(e)=> e.stopPropagation()} display="none"/>}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                    <Flex align="center" justify="flex-end" mb={3}>
                      <ButtonGroup size="sm" isAttached>
                        <Button
                          variant={viewMode==='calendar' ? 'solid' : 'outline'}
                          bg={viewMode==='calendar' ? 'brand.primary' : undefined}
                          color={viewMode==='calendar' ? 'text.onPrimary' : undefined}
                          _hover={{ filter: viewMode==='calendar' ? 'brightness(0.95)' : undefined, borderColor: 'brand.primary', color: viewMode==='calendar' ? 'text.onPrimary' : undefined }}
                          onClick={() => setViewMode('calendar')}
                        >{t('calendar.title')}</Button>
                        <Button
                          variant={viewMode==='list' ? 'solid' : 'outline'}
                          bg={viewMode==='list' ? 'brand.primary' : undefined}
                          color={viewMode==='list' ? 'text.onPrimary' : undefined}
                          _hover={{ filter: viewMode==='list' ? 'brightness(0.95)' : undefined, borderColor: 'brand.primary', color: viewMode==='list' ? 'text.onPrimary' : undefined }}
                          onClick={() => setViewMode('list')}
                        >{t('calendar.list_view')}</Button>
                      </ButtonGroup>
                    </Flex>
                    {viewMode === 'calendar' ? (
                      <>
                        <Flex align="center" justify="space-between" mb={3} gap={2} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                          <IconButton
                            aria-label={t('calendar.previous_month')}
                            size="sm"
                            onClick={() => setMonthRef(addMonths(monthRef, -1))}
                            icon={<ChevronLeftIcon />}
                            variant="outline"
                            _hover={{ bg: 'rgba(0,0,0,0.04)', borderColor: 'brand.primary' }}
                          />
                          <Button
                            size="sm"
                            onClick={() => setMonthRef(new Date())}
                            variant="solid"
                            bg={'brand.primary'}
                            color={'text.onPrimary'}
                            _hover={{ filter: 'brightness(0.95)' }}
                          >{t('calendar.today')}</Button>
                          <IconButton
                            aria-label={t('calendar.next_month')}
                            size="sm"
                            onClick={() => setMonthRef(addMonths(monthRef, 1))}
                            icon={<ChevronRightIcon />}
                            variant="outline"
                            _hover={{ bg: 'rgba(0,0,0,0.04)', borderColor: 'brand.primary' }}
                          />
                        </Flex>
                        <Box overflowX="auto">
                          <Grid templateColumns="repeat(7, 1fr)" gap={3} minW="980px">
                            {weekdayHeaders.map((w: string) => (
                              <Box key={w} textAlign="center" fontWeight="semibold" color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>{w}</Box>
                            ))}
                          </Grid>
                        </Box>
                        <Box overflowX="auto">
                          <Grid templateColumns="repeat(7, 1fr)" gap={{ base: 1, md: 2 }} minW="980px">
                            {weeks.map((day, idx) => {
                              const key = format(day, 'yyyy-MM-dd');
                              const list = byDate.get(key) || [];
                              const faded = !isSameMonth(day, monthRef);
                              const today = format(day, 'yyyy-MM-dd') === pragueTodayStr;
                              return (
                                <Box
                                  key={idx}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={2}
                                  minH="120px"
                                  minW="130px"
                                  bg={today ? 'rgba(59,130,246,0.06)' : calendarDayBg}
                                  borderColor={today ? 'brand.primary' : calendarDayBorder}
                                  opacity={faded ? 0.6 : 1}
                                >
                                  <Flex align="center" justify="space-between" mb={2}>
                                    <Text fontWeight="bold">{format(day, 'd')}</Text>
                                    {!!list.length && (
                                      <Badge bg="brand.primary" color="text.onPrimary" borderRadius="full">{list.length}</Badge>
                                    )}
                                  </Flex>
                                  <Stack spacing={2}>
                                    {(expandedDates[key] ? list : list.slice(0,3)).map((m: MatchItem) => {
                                      const href = mkHref(m);
                                      const isPast = new Date(`${m.date}T${(m.time||'00:00')}:00`).getTime() < Date.now();
                                      const countdown = liveCountdowns[String(m.id)];
                                      const info = getMatchCompInfo(m, c);
                                      return (
                                        <Tooltip
                                          key={m.id}
                                          label={(
                                            <Box>
                                              <Text fontSize="xs">Kategorie: {info.display || '—'}</Text>
                                              {info.alias && info.alias !== info.display && (
                                                <Text fontSize="xs">Alias: {info.alias}</Text>
                                              )}
                                            </Box>
                                          )}
                                          hasArrow
                                          placement="top"
                                          openDelay={200}
                                        >
                                          <Box position="relative" _hover={{ textDecoration: 'none' }} data-href={href || undefined} onMouseDown={handleMatchMouseDown} onClick={() => openMatchModal(m, c)}>
                                            {href && (
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => { if (!(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) e.preventDefault(); }}
                                                onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                                onAuxClick={(e) => { if ((e as any).button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                                style={{ position: 'absolute', inset: 0, zIndex: 1 }}
                                                aria-hidden
                                              />
                                            )}
                                            <Box p={2} borderWidth="1px" borderRadius="md" bg={calendarMatchBg} _hover={{ bg: calendarMatchHoverBg, borderColor: 'brand.primary', cursor: 'pointer' }} textAlign="center">
                                              {!isPast && countdown ? (
                                                <>
                                                  <Flex align="center" justify="center" gap={2} mb={1}>
                                                    {m.home_logo_url && <Image src={m.home_logo_url} alt={m.home} boxSize="18px" borderRadius="full" objectFit="cover" loading="lazy" decoding="async" />}
                                                    <Badge colorScheme="orange">za {countdown}</Badge>
                                                    {m.away_logo_url && <Image src={m.away_logo_url} alt={m.away} boxSize="18px" borderRadius="full" objectFit="cover" loading="lazy" decoding="async" />}
                                                  </Flex>
                                                  <Text fontSize="xs" color="text.secondary">{m.time || '—'}</Text>
                                                </>
                                              ) : (
                                                <>
                                                  <Flex align="center" justify="center" gap={2} mb={1}>
                                                    {m.home_logo_url && <Image src={m.home_logo_url} alt={m.home} boxSize="18px" borderRadius="full" objectFit="cover" loading="lazy" decoding="async" />}
                                                    <Badge colorScheme={isPast && m.score ? (getSentiment(m)?.color || 'gray') : 'gray'}>{isPast && m.score ? m.score : 'vs'}</Badge>
                                                    {m.away_logo_url && <Image src={m.away_logo_url} alt={m.away} boxSize="18px" borderRadius="full" objectFit="cover" loading="lazy" decoding="async" />}
                                                  </Flex>
                                                  <Text fontSize="xs" color="text.secondary">{m.time || '—'}</Text>
                                                </>
                                              )}
                                            </Box>
                                            {href && (
                                              <Link href={href} isExternal onClick={(e)=> e.stopPropagation()} display="none"/>
                                            )}
                                          </Box>
                                        </Tooltip>
                                      );
                                    })}
                                    {list.length > 3 && !expandedDates[key] && (
                                      <Button size="xs" variant="link" colorScheme="gray" onClick={() => setExpandedDates((s) => ({ ...s, [key]: true }))}>
                                        {t('calendar.show_more', { count: list.length - 3 })}
                                      </Button>
                                    )}
                                    {expandedDates[key] && list.length > 3 && (
                                      <Button size="xs" variant="link" colorScheme="gray" onClick={() => setExpandedDates((s) => ({ ...s, [key]: false }))}>
                                        {t('calendar.show_less')}
                                      </Button>
                                    )}
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Grid>
                        </Box>
                    </>
                    ) : (
                      <Stack spacing={4}>
                        {(() => {
                          const keys = Array.from(byDate.keys());
                          const todayStr = pragueTodayStr;
                          const pastKeys = keys.filter(k => k < todayStr).sort().reverse();
                          const futureKeys = keys.filter(k => k >= todayStr).sort();
                          const renderGroup = (dKey: string, highlight: boolean) => {
                            const dayMatches = byDate.get(dKey) || [];
                            return (
                              <Box key={dKey} borderWidth="1px" borderRadius="md" overflow="hidden" bg={listGroupBg} borderColor={listGroupBorder}>
                                <Box
                                  px={3}
                                  py={2}
                                  bg={highlight ? listGroupHeaderBgHighlight : listGroupHeaderBg}
                                  borderLeftWidth="4px"
                                  borderLeftColor={highlight ? 'brand.primary' : listGroupHeaderBorderLeft}
                                >
                                  <Flex align="center" gap={2}>
                                    <Text fontWeight="semibold" color={highlight ? 'brand.primary' : listGroupHeaderText}>
                                      {format(parse(dKey, 'yyyy-MM-dd', new Date()), 'EEEE d. M. yyyy', { locale: cs })}
                                    </Text>
                                    {highlight && (
                                      <Badge colorScheme="blue" variant="subtle" borderRadius="full">{t('calendar.today')}</Badge>
                                    )}
                                  </Flex>
                                </Box>
                                <Stack spacing={3}>
                                  {dayMatches.map((m: MatchItem) => {
                                    const href = mkHref(m);
                                    const isPast = new Date(`${m.date}T${(m.time||'00:00')}:00`).getTime() < Date.now();
                                    const sentiment = isPast ? getSentiment(m) : null;
                                    const countdown = liveCountdowns[String(m.id)];
                                    const info = getMatchCompInfo(m, c);
                                    return (
                                      <Tooltip
                                        key={m.id}
                                        label={(
                                          <Box>
                                            <Text fontSize="xs">Kategorie: {info.display || '—'}</Text>
                                            {info.alias && info.alias !== info.display && (
                                              <Text fontSize="xs">Alias: {info.alias}</Text>
                                            )}
                                          </Box>
                                        )}
                                        hasArrow
                                        placement="top"
                                        openDelay={200}
                                      >
                                        <Box position="relative" _hover={{ textDecoration: 'none' }} data-href={href || undefined} onMouseDown={handleMatchMouseDown} onClick={() => openMatchModal(m, c)}>
                                          {href && (
                                            <a
                                              href={href}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => { e.preventDefault(); }}
                                              onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                              onAuxClick={(e) => { if ((e as any).button === 1) { e.preventDefault(); e.stopPropagation(); try { const w = window.open(href as string, '_blank', 'noopener,noreferrer'); (w as any)?.blur?.(); setTimeout(() => { try { window.focus(); } catch {} }, 0); } catch {} } }}
                                              style={{ position: 'absolute', inset: 0, zIndex: 1 }}
                                              aria-hidden
                                            />
                                          )}
                                          <Flex
                                            align="center"
                                            justify="space-between"
                                            p={3}
                                            borderWidth="1px"
                                            borderRadius="md"
                                            bg={listMatchBg}
                                            borderColor={listMatchBorder}
                                            _hover={{ bg: listMatchHoverBg, borderColor: 'brand.primary', cursor: 'pointer', transform: 'translateY(-2px)', boxShadow: 'md' }}
                                            transition="all 0.2s"
                                            gap={3}
                                          >
                                            <Flex direction="column" minW="100px">
                                              <Text fontWeight="semibold" color={listDateText} fontSize="sm">{m.date}</Text>
                                              <Text color={listDateText} fontSize="sm">{m.time || '—'}</Text>
                                              {m.venue && <Text color={listVenueText} fontSize="xs" mt={1}>{m.venue}</Text>}
                                            </Flex>
                                            
                                            <Flex align="center" gap={3} flex="1">
                                              {/* Home Team */}
                                              <Flex align="center" gap={2} flex="1" justify="flex-end">
                                                <Text fontSize="sm" fontWeight="medium" textAlign="right" color={listDateText}>
                                                  {m.home}
                                                </Text>
                                                {m.home_logo_url && (
                                                  <Image 
                                                    src={m.home_logo_url} 
                                                    alt={m.home} 
                                                    boxSize="32px" 
                                                    borderRadius="full" 
                                                    objectFit="cover"
                                                    border="2px solid"
                                                    borderColor="gray.200"
                                                    loading="lazy"
                                                    decoding="async"
                                                  />
                                                )}
                                              </Flex>
                                              
                                              {/* Score or Countdown */}
                                              <Flex direction="column" align="center" gap={1} minW="80px">
                                                {!isPast && countdown ? (
                                                  <Badge colorScheme="orange" fontSize="sm" px={2}>za {countdown}</Badge>
                                                ) : (
                                                  <Badge colorScheme={isPast && m.score ? (getSentiment(m)?.color || 'gray') : 'gray'} fontSize="md" px={3} py={1}>
                                                    {isPast && m.score ? m.score : 'vs'}
                                                  </Badge>
                                                )}
                                                {sentiment && (
                                                  <Text fontSize="xs" color={`${sentiment.color}.600`} fontWeight="semibold">
                                                    {sentiment.label}
                                                  </Text>
                                                )}
                                              </Flex>
                                              
                                              {/* Away Team */}
                                              <Flex align="center" gap={2} flex="1" justify="flex-start">
                                                {m.away_logo_url && (
                                                  <Image 
                                                    src={m.away_logo_url} 
                                                    alt={m.away} 
                                                    boxSize="32px" 
                                                    borderRadius="full" 
                                                    objectFit="cover"
                                                    border="2px solid"
                                                    borderColor="gray.200"
                                                    loading="lazy"
                                                    decoding="async"
                                                  />
                                                )}
                                                <Text fontSize="sm" fontWeight="medium" textAlign="left" color={listDateText}>
                                                  {m.away}
                                                </Text>
                                              </Flex>
                                            </Flex>
                                          </Flex>
                                          {href && (
                                            <Link href={href} isExternal onClick={(e)=> e.stopPropagation()} display="none"/>
                                          )}
                                        </Box>
                                      </Tooltip>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            );
                          };
                          return (
                            <>
                              {!!pastKeys.length && (
                                <Box>
                                  {!showPast ? (
                                    <Button size="sm" variant="link" onClick={() => setShowPast(true)}>
                                      Zobrazit předchozí zápasy ({pastKeys.reduce((acc, k) => acc + (byDate.get(k)?.length || 0), 0)})
                                    </Button>
                                  ) : (
                                    <Button size="sm" variant="link" onClick={() => setShowPast(false)}>
                                      {t('calendar.hide_past_matches')}
                                    </Button>
                                  )}
                                  {showPast && (
                                    <Stack spacing={4} mt={2}>
                                      {pastKeys.map((k) => renderGroup(k, false))}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                              <Stack spacing={4}>
                                {futureKeys.map((k) => renderGroup(k, k === todayStr))}
                              </Stack>
                            </>
                          );
                        })()}
                      </Stack>
                    )}
                </TabPanel>
              );
            })}
          </TabPanels>
          </Tabs>
        )}

        {/* Match details modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered returnFocusOnClose={false}>
          <ModalContent>
            <ModalHeader>
              {selected ? (selected.match.__compName || selected.comp?.name || 'Detail zápasu') : 'Detail zápasu'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selected && (
                <Stack spacing={4}>
                  {/* Show match status (Výhra/Remíza/Prohra) if determinable; otherwise fall back to competition name */}
                  {(() => {
                    const s = getSentiment(selected.match);
                    if (s) {
                      return (
                        <Flex justify="center">
                          <Badge colorScheme={s.color as any} variant="subtle">{s.label}</Badge>
                        </Flex>
                      );
                    }
                    const compName = selected.comp?.name || selected.match.__compName;
                    // Don't show "Všechny soutěže" badge - only show specific competition names
                    if (compName && compName !== 'Všechny soutěže') {
                      return (
                        <Flex justify="center">
                          <Badge colorScheme="purple">{compName}</Badge>
                        </Flex>
                      );
                    }
                    return null;
                  })()}
                  <Flex align="center" justify="center" gap={3}>
                    {selected.match.home_logo_url && (
                      <Image 
                        src={selected.match.home_logo_url} 
                        alt={selected.match.home} 
                        boxSize="40px" 
                        borderRadius="full"
                        cursor="pointer"
                        onClick={() => handleTeamClick(selected.match.home, selected.match.home_logo_url)}
                        _hover={{ opacity: 0.8, transform: 'scale(1.1)' }}
                        transition="all 0.2s"
                        title={`Klikněte pro zobrazení statistik: ${selected.match.home}`}
                      />
                    )}
                    {(() => {
                      const dt = new Date(`${selected.match.date}T${(selected.match.time || '00:00')}:00`);
                      const isPast = Date.now() >= dt.getTime();
                      const hasScore = Boolean(selected.match.score);
                      
                      // For future matches, always show countdown or "vs" - never the score
                      if (!isPast) {
                        if (modalCountdown.countdownString) {
                          return (
                            <Badge colorScheme="orange" fontSize="md" px={3} py={1}>za {modalCountdown.countdownString}</Badge>
                          );
                        }
                        return (
                          <Badge colorScheme="gray" fontSize="md" px={3} py={1}>vs</Badge>
                        );
                      }
                      
                      // For past matches, show score or "vs"
                      return (
                        <Badge colorScheme={hasScore ? (getSentiment(selected.match)?.color || 'gray') : 'gray'} fontSize="md" px={3} py={1}>
                          {hasScore ? selected.match.score : 'vs'}
                        </Badge>
                      );
                    })()}
                    {selected.match.away_logo_url && (
                      <Image 
                        src={selected.match.away_logo_url} 
                        alt={selected.match.away} 
                        boxSize="40px" 
                        borderRadius="full"
                        cursor="pointer"
                        onClick={() => handleTeamClick(selected.match.away, selected.match.away_logo_url)}
                        _hover={{ opacity: 0.8, transform: 'scale(1.1)' }}
                        transition="all 0.2s"
                        title={`Klikněte pro zobrazení statistik: ${selected.match.away}`}
                      />
                    )}
                  </Flex>
                  {/* Date and Time Display with Countdown */}
                  <Box textAlign="center">
                    <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={1}>
                      {(() => {
                        try {
                          return format(parse(selected.match.date, 'yyyy-MM-dd', new Date()), 'EEEE d. MMMM yyyy', { locale: cs });
                        } catch {
                          return selected.match.date;
                        }
                      })()}
                    </Text>
                    <Text fontSize="md" color="gray.700">
                      {selected.match.time || '—'}
                    </Text>
                  </Box>
                  
                  {/* Enhanced Countdown Display for Upcoming Matches */}
                  {(() => {
                    const dt = new Date(`${selected.match.date}T${(selected.match.time || '00:00')}:00`);
                    const isPast = Date.now() >= dt.getTime();
                    const hasScore = Boolean(selected.match.score);
                    
                    if (!hasScore && !isPast && modalCountdown.isActive && modalCountdown.timeRemaining > 0) {
                      const days = Math.floor(modalCountdown.timeRemaining / (24 * 60 * 60 * 1000));
                      const hours = Math.floor((modalCountdown.timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
                      const minutes = Math.floor((modalCountdown.timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
                      const seconds = Math.floor((modalCountdown.timeRemaining % (60 * 1000)) / 1000);
                      
                      return (
                        <Box 
                          mt={4} 
                          p={4} 
                          bg="orange.50" 
                          borderRadius="lg" 
                          borderWidth="2px" 
                          borderColor="orange.200"
                        >
                          <Text fontSize="sm" fontWeight="semibold" color="orange.800" mb={3} textAlign="center">
                            Zápas začíná za
                          </Text>
                          <Grid 
                            templateColumns={days > 0 ? "repeat(4, 1fr)" : "repeat(3, 1fr)"} 
                            gap={3}
                          >
                            {days > 0 && (
                              <Box textAlign="center">
                                <Box 
                                  bg="white" 
                                  borderRadius="md" 
                                  p={3} 
                                  borderWidth="1px" 
                                  borderColor="orange.300"
                                  boxShadow="sm"
                                >
                                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                                    {days}
                                  </Text>
                                </Box>
                                <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                                  {days === 1 ? 'den' : days < 5 ? 'dny' : 'dní'}
                                </Text>
                              </Box>
                            )}
                            <Box textAlign="center">
                              <Box 
                                bg="white" 
                                borderRadius="md" 
                                p={3} 
                                borderWidth="1px" 
                                borderColor="orange.300"
                                boxShadow="sm"
                              >
                                <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                                  {String(hours).padStart(2, '0')}
                                </Text>
                              </Box>
                              <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                                {hours === 1 ? 'hodina' : hours < 5 ? 'hodiny' : 'hodin'}
                              </Text>
                            </Box>
                            <Box textAlign="center">
                              <Box 
                                bg="white" 
                                borderRadius="md" 
                                p={3} 
                                borderWidth="1px" 
                                borderColor="orange.300"
                                boxShadow="sm"
                              >
                                <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                                  {String(minutes).padStart(2, '0')}
                                </Text>
                              </Box>
                              <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                                {minutes === 1 ? 'minuta' : minutes < 5 ? 'minuty' : 'minut'}
                              </Text>
                            </Box>
                            <Box textAlign="center">
                              <Box 
                                bg="white" 
                                borderRadius="md" 
                                p={3} 
                                borderWidth="1px" 
                                borderColor="orange.300"
                                boxShadow="sm"
                              >
                                <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                                  {String(seconds).padStart(2, '0')}
                                </Text>
                              </Box>
                              <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                                {seconds === 1 ? 'sekunda' : seconds < 5 ? 'sekundy' : 'sekund'}
                              </Text>
                            </Box>
                          </Grid>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                  <Box h="1px" bg="gray.200" />
                  
                  {/* Weather widget for home matches that haven't started */}
                  {(() => {
                    const dt = new Date(`${selected.match.date}T${(selected.match.time || '00:00')}:00`);
                    const isPast = Date.now() >= dt.getTime();
                    const isHomeMatch = clubName && selected.match.home && isClubTeam(selected.match.home);
                    
                    if (isHomeMatch && !isPast) {
                      return (
                        <Box>
                          <MatchWeather
                            matchDateTime={`${selected.match.date}T${selected.match.time || '15:00'}:00`}
                            venue={undefined} // Home matches use club location
                            isHomeMatch={true}
                            matchHasStarted={false}
                            delayLoad={false} // Auto-load when modal opens
                          />
                        </Box>
                      );
                    }
                    return null;
                  })()}
                  
                  <Box h="1px" bg="gray.200" />
                  <Heading as="h3" size="sm">Odběr notifikací pro fanoušky</Heading>
                  <Text fontSize="sm" color="gray.600">Zadejte svůj email a budeme vás informovat o novinkách a zápasech.</Text>
                  <Flex gap={2} align="center">
                    <Input type="email" placeholder="váš@email.cz" value={fanEmail} onChange={(e) => setFanEmail(e.target.value)} />
                    <Button colorScheme="red" onClick={subscribeFan} isLoading={fanSubscribing}>Odebírat</Button>
                  </Flex>
                </Stack>
              )}
            </ModalBody>
            <ModalFooter>
              {selected && (selected.match.facr_link || selected.match.report_url) && (
                <Button 
                  colorScheme="blue" 
                  mr={3}
                  onClick={(e) => {
                    e.preventDefault();
                    // Open in background tab without switching focus
                    const facrLink = selected.match.facr_link || selected.match.report_url;
                    if (facrLink) {
                      const link = document.createElement('a');
                      link.href = facrLink;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      link.style.display = 'none';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                >
                  Detail na FAČR
                </Button>
              )}
              <Button onClick={onClose}>Zavřít</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
      
      {/* Newsletter CTA */}
      <NewsletterCTA />

      {/* Club Modal for team statistics */}
      <ClubModal
        isOpen={isClubModalOpen}
        onClose={() => setIsClubModalOpen(false)}
        club={selectedClub}
        clubType={clubType}
      />
    </MainLayout>
  );
};

export default CalendarPage;
